/**
 * Shared in-memory cache + in-flight request deduplicator for the checklists
 * hooks. Multiple components mount `useChecklists()` / `useChecklistSections()`
 * in parallel (ChecklistsPage, SectionedChecklists, ChecklistWidget, fallback
 * cards…). Without coordination each instance triggers its own network round
 * trip — exactly what we observed in the network panel (12+ identical GETs at
 * mount time, hence the visible "clignotement").
 *
 * This module provides:
 *  • `getCached(key)`   — returns previously fetched data if still fresh.
 *  • `dedupe(key, fn)`  — coalesces concurrent calls into a single in-flight
 *                         promise; later callers await the same response.
 *  • `setCached(key, v)`— writes the result so the next mount can render
 *                         instantly without any network call.
 *  • `invalidate(key?)` — clears entries after a mutation so the next read
 *                         refetches.
 *
 * Cache TTL is intentionally short (5 s) — long enough to absorb the burst of
 * mounts on a single page render and tab refocuses, short enough that real
 * data changes from other devices are picked up quickly.
 */

const TTL_MS = 5_000;

interface CacheEntry<T> {
  value: T;
  ts: number;
}

const cache = new Map<string, CacheEntry<any>>();
const inflight = new Map<string, Promise<any>>();

export function getCached<T>(key: string): T | undefined {
  const e = cache.get(key);
  if (!e) return undefined;
  if (Date.now() - e.ts > TTL_MS) {
    cache.delete(key);
    return undefined;
  }
  return e.value as T;
}

export function setCached<T>(key: string, value: T) {
  cache.set(key, { value, ts: Date.now() });
}

export async function dedupe<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;
  const p = (async () => {
    try {
      const v = await fn();
      setCached(key, v);
      return v;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

/**
 * Invalidate one specific key, all keys whose name starts with `prefix`,
 * or the entire cache when called with no argument.
 */
export function invalidate(prefix?: string) {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const k of Array.from(cache.keys())) {
    if (k.startsWith(prefix)) cache.delete(k);
  }
}
